import { api } from "../api.slice";
import {Product, IProductNew, ProductPicture} from "@/types/product";
import { ProductsParamsType } from "@/lib/products";
import IComment from "@/types/comment";
import PageResponse from "@/types/page-response";
import { apiEndpoints } from "@/utils/api-endpoints";

const productApi = api.injectEndpoints({
    overrideExisting: true,

    endpoints: (builder) => ({
        getDetails: builder.query<Product, string>({
            query: (id) => apiEndpoints.Product.GetDetails.url(id),
            providesTags: (result, error, arg) => [{ type: "ProductDetails", arg }],
        }),

        getProductsUpdateDateTime: builder.query<string, void>({
            query: () => apiEndpoints.Product.GetUpdateDateTime.url,
        }),

        getSuggestions: builder.query<string[], string>({
            query: (searchText) => ({
                url: `/products/suggestions?searchText=${encodeURIComponent(searchText)}`,
                method: "GET",
            }),
        }),

        getProducts: builder.query<IProductNew, ProductsParamsType>({
            query: (queryParams) => {
                const query = new URLSearchParams(queryParams as any).toString();
                return apiEndpoints.Product.GetProducts.url(query);
            },
        }),

        addComment: builder.mutation<void, { productId: number; body: string; stars: number }>({
            query: ({ productId, ...body }) => ({
                url: apiEndpoints.Product.Comments.AddComment.url(productId.toString()),
                method: apiEndpoints.Product.Comments.AddComment.method,
                body,
            }),
            invalidatesTags: ["ProductDetails"],
        }),

        deleteComment: builder.mutation<void, number>({
            query: (commentId) => ({
                url: apiEndpoints.Product.Comments.DeleteComment.url(commentId.toString()),
                method: apiEndpoints.Product.Comments.DeleteComment.method,
            }),
            invalidatesTags: ["Comments", "ProductDetails"],
        }),

        getComments: builder.query<PageResponse<IComment[]>, { searchText: string; page: number }>({
            query: (args) => ({
                url: apiEndpoints.Product.Comments.GetComments.url,
                params: args,
            }),
            providesTags: ["Comments"],
        }),

        getAdminComments: builder.query<PageResponse<IComment[]>, { searchText: string; page: number }>({
            query: (args) => ({
                url: apiEndpoints.Product.Comments.GetAdminComments.url,
                method: apiEndpoints.Product.Comments.GetAdminComments.method,
                params: args,
            }),
            providesTags: ["Comments"],
        }),
        getUserComments: builder.query<
            PageResponse<IComment[]>,
            { searchText: string; page: number; userId: string }
        >({
            query: ({ searchText = "", page = 1, userId }) => ({
                url: apiEndpoints.Product.Comments.GetUserComment.url(userId),
                method: apiEndpoints.Product.Comments.GetUserComment.method,
                params: { searchText, page },
            }),
            providesTags: ["Comments"],
        }),
        getUserCommentsCount: builder.query<number, string>({
            query: (userId) => ({
                url: apiEndpoints.Product.Comments.GetUserCommentsCount.url(userId),
                method: apiEndpoints.Product.Comments.GetUserCommentsCount.method,
            }),
            providesTags: ["Comments"],
        }),

        getAdminCommentsCount: builder.query<number, void>({
            query: () => ({
                url: apiEndpoints.Product.Comments.GetAdminCommentsCount.url,
                method: apiEndpoints.Product.Comments.GetAdminCommentsCount.method,
            }),
            providesTags: ["Comments"],
        }),
        getCommentsCount: builder.query<number, void>({
            query: () => ({
                url: apiEndpoints.Product.Comments.GetCommentsCount.url,
                method: apiEndpoints.Product.Comments.GetCommentsCount.method,
            }),
            providesTags: ["Comments"],
        }),

        getPriceChangesChart: builder.query<{ date: string; price: number }[], number>({
            query: (productId) => apiEndpoints.Product.GetProductPriceChart.url(productId.toString()),
            providesTags: ["Products"],
        }),

        getAllImagesOfProduct: builder.query<ProductPicture[], number>({
            query: (productId) => ({
                url: apiEndpoints.Product.Images.GetImages.url(productId.toString()),
                method: apiEndpoints.Product.Images.GetImages.method,
            }),
            providesTags: ["ProductPictures"],
        }),

        deletePicture: builder.mutation<void, number>({
            query: (id) => ({
                url: apiEndpoints.Product.Images.DeleteImage.url(id.toString()),
                method: apiEndpoints.Product.Images.DeleteImage.method,
            }),
            invalidatesTags: ["ProductPictures", "ProductDetails"],
        }),

        setProductPictureIsActive: builder.mutation<void, { id: number; isActive: boolean }>({
            query: ({ id, isActive }) => ({
                url: apiEndpoints.Product.Images.EditImageActive.url(id.toString(), isActive),
                method: apiEndpoints.Product.Images.EditImageActive.method,
            }),
            invalidatesTags: ["ProductPictures", "ProductDetails"],
        }),

        addProductPictures: builder.mutation<void, { images: FileList; productId: number }>({
            query: ({ images, productId }) => {
                const body = new FormData();
                Array.from(images).forEach((img) => body.append("images", img));
                return {
                    url: apiEndpoints.Product.Images.AddImages.url(productId.toString()),
                    method: apiEndpoints.Product.Images.AddImages.method,
                    body,
                };
            },
            invalidatesTags: ["ProductPictures", "ProductDetails"],
        }),

        downloadPicturesZip: builder.query<{ zipFilePath: string }, number>({
            query: (productId) => ({
                url: apiEndpoints.Product.Images.DownloadImages.url(productId.toString()),
                method: apiEndpoints.Product.Images.DownloadImages.method,
            }),
            providesTags: ["ProductPictures"],
        }),
        addProductOffer: builder.mutation<void, { id: number; discount: number }>({
            query: ({ id, discount }) => ({
                url: apiEndpoints.Product.Offers.AddOffer.url(id.toString(), discount),
                method: apiEndpoints.Product.Offers.AddOffer.method,
            }),
            invalidatesTags: ["ProductDetails"],
        }),

        deleteProductOffer: builder.mutation<void, number>({
            query: (id) => ({
                url: apiEndpoints.Product.Offers.DeleteOffer.url(id.toString()),
                method: apiEndpoints.Product.Offers.DeleteOffer.method,
            }),
            invalidatesTags: ["ProductDetails"],
        }),

    }),
});

export const {
    useGetAllImagesOfProductQuery,
    useAddCommentMutation,
    useDeleteCommentMutation,
    useGetCommentsQuery,
    useGetPriceChangesChartQuery,
    useDeletePictureMutation,
    useSetProductPictureIsActiveMutation,
    useAddProductPicturesMutation,
    useLazyDownloadPicturesZipQuery,
    useGetSuggestionsQuery,
    useGetCommentsCountQuery,
    useAddProductOfferMutation,
    useDeleteProductOfferMutation,
    useGetAdminCommentsQuery,
    useGetUserCommentsQuery,
} = productApi;

